// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

/**
 * Cached bid increment service to prevent excessive database queries
 * Caches increment rules for 60 seconds
 */

interface BidIncrementRule {
  min_amount: number;
  max_amount: number | null;
  increment: number;
}

interface AuctionIncrementCacheEntry {
  rules: BidIncrementRule[];
  timestamp: number;
}

interface GlobalIncrementCacheEntry {
  rules: BidIncrementRule[];
  timestamp: number;
}

interface IncrementCache {
  global: GlobalIncrementCacheEntry;
  auctions: Map<string, AuctionIncrementCacheEntry>;
}

const CACHE_DURATION = 10000; // 10 seconds to respect rapid admin updates
let cache: IncrementCache = {
  global: {
    rules: [],
    timestamp: 0,
  },
  auctions: new Map(),
};

/**
 * Load global bid increments into cache
 */
async function loadGlobalIncrements(): Promise<BidIncrementRule[]> {
  const { data, error } = await supabase
    .from('bid_increments')
    .select('*')
    .order('min_amount', { ascending: true });
  
  if (error || !data) {
    console.error('Error loading global increments:', error);
    return [];
  }
  
  return data as BidIncrementRule[];
}

/**
 * Load auction-specific bid increments into cache
 */
async function loadAuctionIncrements(auctionId: string): Promise<BidIncrementRule[]> {
  const { data, error } = await supabase
    .from('auction_bid_increments')
    .select('min_amount, max_amount, increment')
    .eq('auction_id', auctionId)
    .order('min_amount', { ascending: true });
  
  if (error || !data) {
    return []; // No auction-specific increments
  }
  
  return data as BidIncrementRule[];
}

/**
 * Refresh cache if needed
 */
async function ensureGlobalCacheFresh(): Promise<void> {
  const now = Date.now();
  
  // Check if cache is still fresh
  if (cache.global.timestamp > 0 && (now - cache.global.timestamp) < CACHE_DURATION) {
    return; // Cache is still fresh
  }
  
  // Reload global increments
  cache.global = {
    rules: await loadGlobalIncrements(),
    timestamp: now,
  };

  // Global changes invalidate auction-specific entries to prevent stale rules
  cache.auctions.clear();
  
  console.log('✅ Bid increment cache refreshed with', cache.global.rules.length, 'global rules');
}

async function ensureAuctionCacheFresh(auctionId: string): Promise<BidIncrementRule[]> {
  const now = Date.now();
  const existing = cache.auctions.get(auctionId);

  if (!existing || (now - existing.timestamp) >= CACHE_DURATION) {
    const rules = await loadAuctionIncrements(auctionId);
    cache.auctions.set(auctionId, {
      rules,
      timestamp: now,
    });
    console.log(`✅ Bid increment cache ${existing ? 'updated' : 'initialized'} for auction ${auctionId} with ${rules.length} rules`);
    return rules;
  }

  return existing.rules;
}

/**
 * Get bid increment for an amount from cache
 * Uses Admin Dashboard bid increment system (bid_increments and auction_bid_increments tables)
 */
export async function getCachedBidIncrement(amount: number, auctionId?: string): Promise<number> {
  // Ensure cache is fresh
  await ensureGlobalCacheFresh();
  
  // Check auction-specific increments first (from Admin Dashboard)
  if (auctionId) {
    const auctionRules = await ensureAuctionCacheFresh(auctionId);
    
    // CRITICAL FIX: Search rules in REVERSE order (highest min_amount first)
    // This ensures that at boundary values (e.g., $500), we use the HIGHER range's increment
    const sortedRules = [...auctionRules].sort((a, b) => b.min_amount - a.min_amount);
    const auctionRule = sortedRules.find(rule => 
      amount >= rule.min_amount && (rule.max_amount === null || amount < rule.max_amount)
    );
    
    if (auctionRule) {
      console.log(`✅ Using auction-specific increment from Admin Dashboard: $${auctionRule.increment} for amount $${amount} (range: $${auctionRule.min_amount}-${auctionRule.max_amount || '∞'})`);
      return auctionRule.increment;
    }
  }
  
  // Check global increments (from Admin Dashboard bid_increments table)
  // CRITICAL FIX: Search rules in REVERSE order (highest min_amount first)
  // This ensures that at boundary values (e.g., $500), we use the HIGHER range's increment
  const sortedGlobalRules = [...cache.global.rules].sort((a, b) => b.min_amount - a.min_amount);
  const globalRule = sortedGlobalRules.find(rule => 
    amount >= rule.min_amount && (rule.max_amount === null || amount < rule.max_amount)
  );
  
  if (globalRule) {
    console.log(`✅ Using global increment from Admin Dashboard: $${globalRule.increment} for amount $${amount} (range: $${globalRule.min_amount}-${globalRule.max_amount || '∞'})`);
    return globalRule.increment;
  }
  
  // Fallback (should never happen if admin configured increments properly)
  const fallbackIncrement = Math.max(25, Math.round(amount * 0.1));
  console.warn(`⚠️ No Admin Dashboard increment rule found for $${amount}, using fallback: $${fallbackIncrement}. Please configure bid increments in Admin Dashboard.`);
  return fallbackIncrement;
}

/**
 * Clear cache (useful for testing or when rules change)
 */
export function clearBidIncrementCache(): void {
  cache = {
    global: {
      rules: [],
      timestamp: 0,
    },
    auctions: new Map(),
  };
  console.log('🧹 Bid increment cache cleared');
}

/**
 * Preload cache for better performance
 */
export async function preloadBidIncrementCache(auctionId?: string): Promise<void> {
  await ensureGlobalCacheFresh();
  
  if (auctionId) {
    const rules = await ensureAuctionCacheFresh(auctionId);
    console.log(`✅ Preloaded ${rules.length} auction-specific rules for auction ${auctionId}`);
  }
}

